<template>
  <div class="relative flex items-center space-x-2">
    <button
      v-if="column.filterComponent.component"
      type="button"
      class="h-6 w-6 cursor-pointer rounded-sm"
      :class="filterIconClasses"
      :data-cy="`table-header-filter-${column.key}-filter`"
    >
      <icon-base
        :icon="IconFilter"
        height="12"
        width="12"
        viewBox="0 0 16 16"
      />
      <div
        v-show="toggleFilter"
        class="absolute z-50"
        :class="{ 'pointer-events-none': !toggleFilter }"
      >
        <component
          :is="column.filterComponent.component"
          v-if="
            initialComponentRender || (!initialComponentRender && toggleFilter)
          "
          v-on-click-outside="onClickOutsideHandler"
          v-bind="{
            ...(column.filterComponent.props || {}),
          }"
          :toggle-from-parent="toggleFilter"
          :is-loading="isLoading"
          :model-value:is-exclude-mode-enabled="
            column.filterComponent.props.isExcludeModeEnabled
          "
          @update:model-value="column.filterComponent.onValueChange"
          @change="column.filterComponent.onChange"
          @loaded-more="column.filterComponent.loadedMore"
          @options-search="column.filterComponent.optionsSearch"
          @toggle-visible="handleFilterVisibilityChange"
          @update:is-exclude-mode-enabled="
            column.filterComponent.excludeModeChanged
          "
        ></component>
      </div>
    </button>
    <span class="flex items-center space-x-1">
      <span
        v-if="
          column.filterComponent.numberOfFilters &&
          column.filterComponent.plural
        "
        class="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-center text-xxs leading-5 text-white"
      >
        <span>
          {{ column.filterComponent.numberOfFilters }}
        </span>
      </span>
      <span>
        {{ columnLabel }}
      </span>
    </span>
    <div
      v-if="hasFiltersSet && column.filterComponent.onClear"
      class="translate-y-0-25 transform"
      :data-cy="`clear-filter-${props.column.filterComponent.props.name}`"
      @click.stop="column.filterComponent.onClear"
    >
      <icon-base :icon="IconXCircle" />
    </div>

    <span
      v-if="typeof column.filterComponent.sortByChange === 'function'"
      class="translate-y-0-25 transform"
      :data-cy="`table-header-filter-${column.key}-sort`"
      @click.prevent.stop="
        column.filterComponent.sortByChange(column.filterComponent.props.name)
      "
    >
      <div>
        <icon-base
          v-if="sortByActive"
          :data-cy="`sort-by-${props.column.filterComponent.props.name}`"
          :icon="IconSortArrowActive"
          height="18"
          width="18"
          :class="{
            'rotate-180 transform': rotateSortByArrow,
          }"
        />
        <icon-base
          v-else
          :data-cy="`sort-by-${props.column.filterComponent.props.name}`"
          :icon="IconSortArrow"
          height="18"
          width="18"
        />
      </div>
    </span>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import IconFilter from "@/components/icons/IconFilter.vue";
import IconSortArrow from "@/components/icons/IconSortArrow.vue";
import IconSortArrowActive from "@/components/icons/IconSortArrowActive.vue";
import IconXCircle from "@/components/icons/IconXCircle.vue";

const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  toggleFilter: {
    type: Boolean,
    default: false,
  },
  // when false will only render toggled inner component when toggled to show
  initialComponentRender: {
    type: Boolean,
    default: true,
  },
  scrollableElementId: {
    type: String,
    default: "appWrapper",
  },
});

const emit = defineEmits<{
  (eventName: "closeFilter"): void;
}>();

const { dispatch } = useStore();
const isLoading = ref(false);

const filterIconClasses = computed(() => {
  if (props.toggleFilter) {
    return "bg-primary/10 text-primary";
  }
  const className = hasFiltersSet.value ? "text-primary" : "text-label";
  return `${className} hover:bg-grey-hover`;
});

const hasFiltersSet = computed(() => {
  return (
    props.column?.filterComponent?.hasFiltersSet ||
    props.column?.filterComponent?.numberOfFilters > 0
  );
});

const columnLabel = computed(() =>
  props.column?.filterComponent?.numberOfFilters > 1 ||
  (!props.column?.filterComponent?.numberOfFilters && hasFiltersSet.value)
    ? props.column?.filterComponent?.plural
    : props.column?.label
);

const sortByActive = computed(
  () =>
    !!props.column?.filterComponent?.sortBy?.includes(
      props.column?.filterComponent?.props?.name
    )
);

const rotateSortByArrow = computed(
  () => !props.column?.filterComponent?.sortBy?.includes("-")
);

const onClickOutsideHandler = [
  () => {
    if (props.initialComponentRender && !props.toggleFilter) {
      return;
    }
    emit("closeFilter");
  },
  { capture: props.initialComponentRender },
];

const handleFilterVisibilityChange = (value: boolean) => {
  if (!value) {
    emit("closeFilter");
  }
};

watch(
  () => props.toggleFilter,
  async (newToggleVal) => {
    if (props.column?.filterComponent?.lazyAction?.action && newToggleVal) {
      isLoading.value = true;
      try {
        await dispatch(
          props.column.filterComponent.lazyAction.action,
          props.column.filterComponent.lazyAction.payload
        );
      } finally {
        isLoading.value = false;
      }
    }
  }
);
</script>
